interface TimelineLoan {
  id: number
  lender_name: string
  days_to_maturity: number
  maturity_urgency: 'red' | 'yellow' | 'green'
  loan_amount: number
}

interface Props {
  loans: TimelineLoan[]
}

const COLORS = {
  red: '#ef4444',
  yellow: '#f59e0b',
  green: '#22c55e',
} as const

export default function MaturityTimeline({ loans }: Props) {
  if (loans.length === 0) return null

  const sorted = [...loans].sort((a, b) => a.days_to_maturity - b.days_to_maturity)
  const maxDays = Math.max(...sorted.map((l) => l.days_to_maturity), 1)
  const totalBalance = sorted.reduce((sum, l) => sum + l.loan_amount, 0)

  return (
    <div className="bg-white rounded-xl border border-dl-border p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Maturity Timeline</p>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1 text-[10px] text-gray-500">
            <span className="w-2 h-2 rounded-full bg-red-500" /> &lt;30d
          </span>
          <span className="flex items-center gap-1 text-[10px] text-gray-500">
            <span className="w-2 h-2 rounded-full bg-amber-500" /> 30-90d
          </span>
          <span className="flex items-center gap-1 text-[10px] text-gray-500">
            <span className="w-2 h-2 rounded-full bg-emerald-500" /> &gt;90d
          </span>
        </div>
      </div>

      {/* Bar */}
      <div className="h-6 rounded-full overflow-hidden flex bg-gray-100">
        {sorted.map((loan) => {
          const widthPct = Math.max((loan.loan_amount / totalBalance) * 100, 3)
          return (
            <div
              key={loan.id}
              className="h-full relative group cursor-default"
              style={{
                width: `${widthPct}%`,
                backgroundColor: COLORS[loan.maturity_urgency],
              }}
              title={`${loan.lender_name}: ${loan.days_to_maturity}d`}
            >
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-10">
                <div className="bg-gray-900 text-white text-[10px] rounded px-2 py-1 whitespace-nowrap shadow">
                  {loan.lender_name} — {loan.days_to_maturity}d
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Labels below */}
      <div className="flex justify-between mt-2">
        <span className="text-[10px] text-gray-400">Now</span>
        <span className="text-[10px] text-gray-400">{maxDays}d</span>
      </div>
    </div>
  )
}
